import {
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from 'react'

import {
  Box,
  Typography,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Button,
  ListItem,
  Popper,
  Paper,
  alpha,
  Divider,
  ClickAwayListener,
} from '@mui/material'
import { useTransition, animated } from '@react-spring/web'
import {
  IconLogin2,
  IconMapPin,
  IconSettings,
  IconUserPlus,
  IconChevronDown,
} from '@tabler/icons-react'

import { navigate, routes } from '@redwoodjs/router'

import { useAuth } from 'src/auth'
import DayTooltip from 'src/components/DayTooltip/DayTooltip'
import DomainAvatar from 'src/components/DomainAvatar/DomainAvatar'
import InviteWorkspaceMemberDialog from 'src/components/InviteWorkspaceMemberDialog/InviteWorkspaceMemberDialog'
import Row from 'src/components/Row/Row'
import { useNavigation } from 'src/contexts/NavigationContext'
import { DayContext } from 'src/lib/dayContext'
import { shadows } from 'src/lib/style'

const avatarStyles = {
  height: '22px',
  width: '22px',
  borderRadius: '100px',
  flexShrink: 0,
}

const rowStyles = {
  justifyContent: 'space-between',
  width: '100%',
}

const workspaceNameStyles = {
  fontSize: '12px',
  fontWeight: 600,
  lineHeight: '140%',
  letterSpacing: '-0.34px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

const emptyBoxStyles = {
  width: '100%',
  height: '56px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'row',
  borderRadius: 0,
}

const inviteMenuItemStyles = {
  px: '6px',
  py: 0,
  mt: '12px',
  minHeight: '32px',
  height: '32px',
  width: '100%',
  mb: '12px',
  backgroundColor: (theme) => theme.palette.background.paper,
  border: (theme) => `1px solid ${theme.palette.divider}`,
  borderRadius: '50px',
  '&:hover': {
    boxShadow: (theme) => shadows[theme.palette?.mode]?.sm,
    backgroundColor: (theme) => theme.palette.background.paper,
  },
  '& .MuiListItemIcon-root': {
    minWidth: '0px',
    pl: '4px',
    mr: '14px',
  },
  '& .MuiListItemText-root': {
    '& .MuiTypography-root': {
      fontSize: '12px',
      fontWeight: 600,
      letterSpacing: '-0.34px',
    },
  },
}

const workspaceBoxStyles = {
  fontSize: '12px',
  transition: 'all 0.22s ease',
}

const workspaceImageStyles = {
  width: '24px',
  height: '24px',
  objectFit: 'cover',
  borderRadius: '100px',
}

const marginRightStyles = {
  mr: '6px',
  '& .domainavatar, .MuiAvatar-root': {
    m: 0,
  },
}

const workspacePopperPaperSx = {
  width: 420,
  marginLeft: 0,
  overflow: 'hidden',
  boxShadow: (theme) => shadows[theme.palette?.mode]?.lg,
  borderRadius: '12px',
  zIndex: 1300,

  background: (theme) => alpha(theme.palette.background.paper, 0.8),
  backdropFilter: 'blur(10px)',
  px: '12px',
  py: 0,
}

const workspaceListSx = {
  p: 0,
}

const workspaceListItemSx = {
  pl: 0.5,
  pr: '12px',
  py: 0,
  mt: '12px',
  minHeight: '32px',
  height: '32px',
  width: '100%',
  backgroundColor: (theme) => theme.palette.background.paper,
  border: (theme) => `1px solid ${theme.palette.divider}`,
  borderRadius: '50px',
  '&:hover': {
    boxShadow: (theme) => shadows[theme.palette?.mode]?.sm,
  },

  '& .MuiDivider-root': {
    px: 0,
    mx: 0,
  },

  '& .workspace-selected-indicator': {
    width: '88px',
    maxWidth: '88px',
    minWidth: '88px',
    display: 'flex',
    flexGrow: 0,
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '4px',

    '& .workspace-selected-indicator-row': {
      width: '100%',
      justifyContent: 'flex-start',
      pl: '4px',
      gap: 0.5,
      flexShrink: 0,
    },

    '& .workspace-selected-indicator-icon': {
      color: (theme) => theme.palette.primary.main,
      height: '12px',
      width: '12px',
      opacity: 0.6,
    },

    '& .workspace-selected-indicator-text': {
      fontSize: '10px',
      fontWeight: 600,
      letterSpacing: '-0.22px',
      color: (theme) => theme.palette.text.primary,
      opacity: 0.6,
    },

    '& .workspace-dropdown-settings-button': {
      minWidth: '88px',
      width: '88px',
      maxWidth: '88px',
      flexShrink: 0,
    },
  },
}

const switchButtonSx = {
  minWidth: '0px',
}

const settingsButtonSx = {
  minWidth: '0px',
}

function getWorkspaceLogo(workspace) {
  if (workspace.isDefault) return `${process.env.HOST}/logos/logo-blue.png`

  return `${process.env.HOST}/logos/logo-blue-inverse.png`
}

export default function WorkspaceDropdown({
  forceExpanded = false,
}: {
  forceExpanded?: boolean
}) {
  const { currentUser: user } = useAuth()
  const anchorRef = useRef<HTMLButtonElement>(null)
  const { selectedWorkspace, setSelectedWorkspace, workspaces } =
    useContext(DayContext)
  const { primaryNavCollapsed, primaryDrawerHovered } = useNavigation()

  const workspace = workspaces?.find(
    (workspace) => workspace.id === selectedWorkspace
  )
  const roles = workspace?.roles

  const [openInviteDialog, setOpenInviteDialog] = useState(false)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [cachedLogoUrl, setCachedLogoUrl] = useState<string | null>(null)

  // Load cached logo URL on initial load
  useEffect(() => {
    const cached = localStorage.getItem('currentWorkspaceLogoUrl')
    if (cached) {
      setCachedLogoUrl(cached)
    }
  }, [])

  // Update cache when workspace changes
  useEffect(() => {
    if (workspace) {
      const logoUrl = getWorkspaceLogo(workspace)
      localStorage.setItem('currentWorkspaceLogoUrl', logoUrl)
      setCachedLogoUrl(logoUrl)
    }
  }, [workspace])

  // Helper function to get logo URL with caching fallback
  const getLogoUrl = useCallback(
    (ws = workspace) => {
      if (ws) {
        return getWorkspaceLogo(ws)
      }
      // Fallback to cached URL if workspace not available yet
      return cachedLogoUrl || `${process.env.HOST}/logos/logo-blue.png`
    },
    [workspace, cachedLogoUrl]
  )

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const isExpanded = useMemo(() => {
    return !primaryNavCollapsed || primaryDrawerHovered || forceExpanded
  }, [primaryNavCollapsed, primaryDrawerHovered, forceExpanded])

  const isOpen = Boolean(anchorEl)

  // Filter active workspaces for the menu
  const activeWorkspaces =
    workspaces?.filter(({ status }) =>
      ['ACTIVE', 'INVITED'].includes(status)
    ) || []

  // Create items array including workspaces, settings, and invite button
  const menuItems = isOpen
    ? [
        ...activeWorkspaces.map((workspace) => ({
          type: 'workspace',
          data: workspace,
        })),
        { type: 'divider', data: null },
        { type: 'settings', data: null },
        { type: 'invite', data: null },
      ]
    : []

  // Unified transition animation for all items with trail effect
  const menuTransitions = useTransition(menuItems, {
    keys: (item) => (item.type === 'workspace' ? item.data.id : item.type),
    from: { transform: 'scale(0.92) translateY(-12px)' },
    enter: { transform: 'scale(1) translateY(0px)' },
    leave: { transform: 'scale(0.92) translateY(-12px)' },
    trail: 40, // Consistent trail for all items
    config: {
      tension: 450,
      friction: 20, // Even lower friction = more bounce
    },
  })

  const currentUserRoleId = (workspace?.members || []).find(
    (member) => member.id === user.id
  )?.roleId

  const currentUserRole = (roles || []).find(
    (role) => role.id === currentUserRoleId
  )

  const canInviteMembers = (currentUserRole?.permissions || []).some(
    ({ type, permission }) => type === 'USERS' && permission === 'manage'
  )

  const handleSettingsClick = useCallback(
    (e, workspace) => {
      e.stopPropagation()
      e.preventDefault()
      if (selectedWorkspace !== workspace.id) {
        setSelectedWorkspace(workspace.id)
        setAnchorEl(null)
      }
      navigate(routes.workspaceSettings({ id: workspace.id }))
      setAnchorEl(null)
    },
    [selectedWorkspace, setSelectedWorkspace]
  )

  if (!selectedWorkspace || !workspaces?.length) {
    return <Box sx={emptyBoxStyles} />
  }

  return (
    <Box>
      <Box
        ref={anchorRef}
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: !isExpanded ? '48px' : '100%',
          pr: !isExpanded ? 0 : 2,
          pl: !isExpanded ? '12px' : '20px',
          py: 1,
          cursor: 'pointer',
          transition: 'all 250ms ease',
        }}
      >
        <Row
          className="workspace-avatar"
          sx={{
            gap: !isExpanded ? 0 : '10px',
            width: '100%',
            alignItems: 'center',
            justifyContent: !isExpanded ? 'center' : 'flex-start',
            pt: '3px',
          }}
        >
          {workspace?.domains?.length > 0 && workspace.domains[0].domain ? (
            <DomainAvatar
              domain={workspace.domains[0].domain}
              size={22}
              borderRadius={100}
              sx={avatarStyles}
            />
          ) : (
            <Box
              component="img"
              src={getLogoUrl(workspace)}
              alt={workspace.name}
              sx={avatarStyles}
            />
          )}
          {isExpanded && (
            <>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  letterSpacing: '-0.5px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '150px',
                  fontFamily: 'Poppins',
                }}
              >
                {workspace?.name || 'Unknown'}
              </Typography>
              <IconChevronDown
                size={16}
                style={{
                  transition: 'transform 200ms ease',
                  transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              />
            </>
          )}
        </Row>
      </Box>

      {isOpen && (
        <ClickAwayListener onClickAway={handleClose}>
          <Popper
            open={isOpen}
            anchorEl={anchorRef.current}
            placement="bottom-start"
            sx={{ zIndex: 1300 }}
            modifiers={[
              {
                name: 'offset',
                options: {
                  offset: [24, 0],
                },
              },
            ]}
          >
            <Paper sx={workspacePopperPaperSx}>
              <Box sx={workspaceListSx}>
                {menuTransitions((styles, item) =>
                  item.type === 'workspace' ? (
                    <animated.div
                      key={item.data.id}
                      style={styles}
                    >
                      <ListItem sx={workspaceListItemSx}>
                        <Row sx={rowStyles}>
                          <Row gap={0.5}>
                            <Box sx={marginRightStyles}>
                              {item.data?.domains?.length > 0 &&
                              item.data.domains[0].domain ? (
                                <DomainAvatar
                                  domain={item.data.domains[0].domain}
                                  size={24}
                                  borderRadius={100}
                                />
                              ) : (
                                <Box
                                  component="img"
                                  src={getLogoUrl(item.data)}
                                  alt={item.data.name}
                                  sx={workspaceImageStyles}
                                />
                              )}
                            </Box>
                            <Box sx={workspaceBoxStyles}>
                              <Typography sx={workspaceNameStyles}>
                                {item.data.name || 'Unknown'}
                              </Typography>
                            </Box>
                          </Row>
                          <Row gap={1}>
                            <Divider
                              orientation="vertical"
                              flexItem={true}
                            />
                            <Box className="workspace-selected-indicator">
                              {item.data.id === selectedWorkspace ? (
                                <Row className="workspace-selected-indicator-row">
                                  <IconMapPin
                                    size={14}
                                    className="workspace-selected-indicator-icon"
                                  />
                                  <Typography className="workspace-selected-indicator-text">
                                    You are here
                                  </Typography>
                                </Row>
                              ) : (
                                <DayTooltip
                                  title={`Switch to ${item.data.name}`}
                                >
                                  <Button
                                    onClick={() => {
                                      setSelectedWorkspace(item.data.id)
                                      setAnchorEl(null)
                                    }}
                                    size="small"
                                    sx={switchButtonSx}
                                    startIcon={<IconLogin2 size={14} />}
                                  >
                                    Enter
                                  </Button>
                                </DayTooltip>
                              )}
                            </Box>

                            <Divider
                              orientation="vertical"
                              flexItem={true}
                            />
                            <DayTooltip
                              title={`Manage Settings for ${item.data.name}`}
                            >
                              <Button
                                onClick={(e) =>
                                  handleSettingsClick(e, item.data)
                                }
                                size="small"
                                sx={settingsButtonSx}
                                startIcon={<IconSettings size={14} />}
                                className="workspace-dropdown-settings-button"
                              >
                                Settings
                              </Button>
                            </DayTooltip>
                          </Row>
                        </Row>
                      </ListItem>
                    </animated.div>
                  ) : item.type === 'divider' ? (
                    <animated.div
                      key="divider"
                      style={styles}
                    >
                      <Divider sx={{ my: 1 }} />
                    </animated.div>
                  ) : item.type === 'settings' ? (
                    <animated.div
                      key="settings"
                      style={styles}
                    >
                      <ListItemButton
                        sx={inviteMenuItemStyles}
                        onClick={(e) => {
                          e.stopPropagation()
                          e.preventDefault()
                          navigate(
                            routes.workspaceSettings({
                              id: selectedWorkspace,
                              tab: 'members',
                            })
                          )
                          setAnchorEl(null)
                        }}
                      >
                        <ListItemIcon>
                          <IconSettings size={16} />
                        </ListItemIcon>
                        <ListItemText>Workspace settings</ListItemText>
                      </ListItemButton>
                    </animated.div>
                  ) : item.type === 'invite' ? (
                    <animated.div
                      key="invite"
                      style={styles}
                    >
                      <ListItemButton
                        sx={inviteMenuItemStyles}
                        onClick={(e) => {
                          e.stopPropagation()
                          e.preventDefault()
                          setOpenInviteDialog(true)
                          setAnchorEl(null)
                        }}
                      >
                        <ListItemIcon>
                          <IconUserPlus size={16} />
                        </ListItemIcon>
                        <ListItemText>Invite a team member</ListItemText>
                      </ListItemButton>
                    </animated.div>
                  ) : null
                )}
              </Box>
            </Paper>
          </Popper>
        </ClickAwayListener>
      )}

      {canInviteMembers && (
        <InviteWorkspaceMemberDialog
          title="Invite a team member"
          workspaceId={workspace.id}
          allRoles={roles}
          open={openInviteDialog}
          onClose={() => {
            setOpenInviteDialog(false)
          }}
        />
      )}
    </Box>
  )
}
